import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "@/auth/AuthContext"
import logo from "@/assets/Accelera_logo.svg"
import { gsap } from "gsap"
import Lottie from "lottie-react"
import { useEffect, useRef, useState } from "react"
import { API_ENDPOINTS, getApiUrl } from '@/config'

// shadcn/ui components
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

import { cn } from "@/lib/utils"
import { Icons } from "@/components/icons"

// Lottie animation for navigation
const navAnimation = {
  v: "5.7.4",
  fr: 30,
  ip: 0,
  op: 60,
  w: 200,
  h: 200,
  nm: "Navigation",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "Nav Circle",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 1, k: [
          { i: { x: [0.667], y: [1] }, o: { x: [0.333], y: [0] }, t: 0, s: [0] },
          { t: 59, s: [360] }
        ], ix: 10 },
        p: { a: 0, k: [100, 100, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 1, k: [
          { i: { x: [0.667, 0.667, 0.667], y: [1, 1, 1] }, o: { x: [0.333, 0.333, 0.333], y: [0, 0, 0] }, t: 0, s: [50, 50, 100] },
          { i: { x: [0.667, 0.667, 0.667], y: [1, 1, 1] }, o: { x: [0.333, 0.333, 0.333], y: [0, 0, 0] }, t: 20, s: [100, 100, 100] },
          { t: 59, s: [80, 80, 100] }
        ], ix: 6 }
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              d: 1,
              ty: "el",
              s: { a: 0, k: [60, 60], ix: 2 },
              p: { a: 0, k: [0, 0], ix: 3 },
              nm: "Ellipse Path 1",
              mn: "ADBE Vector Shape - Ellipse",
              hd: false
            },
            {
              ty: "fl",
              c: { a: 0, k: [0.3, 0.7, 1, 1], ix: 4 },
              o: { a: 0, k: 100, ix: 5 },
              r: 1,
              bm: 0,
              nm: "Fill 1",
              mn: "ADBE Vector Graphic - Fill",
              hd: false
            }
          ],
          nm: "Ellipse 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false
        }
      ],
      ip: 0,
      op: 60,
      st: 0,
      bm: 0
    }
  ],
  markers: []
};

// Loading animation
const loadingAnimation = {
  v: "5.7.4",
  fr: 60,
  ip: 0,
  op: 180,
  w: 400,
  h: 400,
  nm: "Loading",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "Spinner",
      sr: 1,
      ks: {
        o: { a: 0, k: 100, ix: 11 },
        r: { a: 1, k: [
          { i: { x: [0.667], y: [1] }, o: { x: [0.333], y: [0] }, t: 0, s: [0] },
          { t: 179, s: [720] }
        ], ix: 10 },
        p: { a: 0, k: [200, 200, 0], ix: 2 },
        a: { a: 0, k: [0, 0, 0], ix: 1 },
        s: { a: 0, k: [100, 100, 100], ix: 6 }
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            {
              d: 1,
              ty: "el",
              s: { a: 0, k: [100, 100], ix: 2 },
              p: { a: 0, k: [0, 0], ix: 3 },
              nm: "Ellipse Path 1",
              mn: "ADBE Vector Shape - Ellipse",
              hd: false
            },
            {
              ty: "st",
              c: { a: 0, k: [0.2, 0.6, 1, 1], ix: 3 },
              o: { a: 0, k: 100, ix: 4 },
              w: { a: 0, k: 8, ix: 5 },
              lc: 2,
              lj: 1,
              ml: 4,
              bm: 0,
              d: [
                {
                  n: "d",
                  nm: "dash",
                  v: { a: 0, k: 20, ix: 1 }
                },
                {
                  n: "g",
                  nm: "gap",
                  v: { a: 0, k: 20, ix: 2 }
                }
              ],
              nm: "Stroke 1",
              mn: "ADBE Vector Graphic - Stroke",
              hd: false
            }
          ],
          nm: "Ellipse 1",
          np: 3,
          cix: 2,
          bm: 0,
          ix: 1,
          mn: "ADBE Vector Group",
          hd: false
        }
      ],
      ip: 0,
      op: 180,
      st: 0,
      bm: 0
    }
  ],
  markers: []
};

export default function SidePanel() {
  const { user, logout, isAdminEmail, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [mobilePrevOpen, setMobilePrevOpen] = useState(false)
  const [hasAssignedLeads, setHasAssignedLeads] = useState(false)
  const [leadsCheckLoading, setLeadsCheckLoading] = useState(false)
  const [leadsCheckCompleted, setLeadsCheckCompleted] = useState(false)
  const sidebarRef = useRef(null)
  const logoRef = useRef(null)
  const navItemsRef = useRef([])
  const mobileNavRef = useRef([])

  // close mobile drawer on route change
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Check if agent has assigned leads - optimized version
  useEffect(() => {
    async function checkAssignedLeads() {
      if (!user || user.role !== "agent") {
        setHasAssignedLeads(false)
        setLeadsCheckCompleted(true)
        return
      }
      
      // If we already have pages assigned, show the button immediately
      if (user?.pages && user.pages.length > 0) {
        setHasAssignedLeads(true)
        setLeadsCheckCompleted(true)
        return
      }
      
      // If we already completed the check, don't check again
      if (leadsCheckCompleted) {
        return
      }
      
      setLeadsCheckLoading(true)
      try {
        const { auth } = await import('@/auth/firebase')
        if (!auth.currentUser) {
          setHasAssignedLeads(false)
          setLeadsCheckCompleted(true)
          return
        }

        // Try to get token without force refresh first
        let token
        try {
          token = await auth.currentUser.getIdToken(false)
        } catch (tokenError) {
          console.error('Error getting cached token:', tokenError)
          // Only try force refresh if not a quota error
          if (!tokenError.message?.includes('quota')) {
            token = await auth.currentUser.getIdToken(true)
          } else {
            setHasAssignedLeads(false)
            setLeadsCheckCompleted(true)
            return
          }
        }

        const response = await fetch(getApiUrl(`${API_ENDPOINTS.LEADS}/my-leads?source=all`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const leads = await response.json()
          setHasAssignedLeads(leads && leads.length > 0)
        } else {
          setHasAssignedLeads(false)
        }
      } catch (error) {
        console.error('Error checking assigned leads:', error)
        setHasAssignedLeads(false)
      } finally {
        setLeadsCheckLoading(false)
        setLeadsCheckCompleted(true)
      }
    }

    checkAssignedLeads()
  }, [user, leadsCheckCompleted])


  // Animation effects
  useEffect(() => {
    if (!loading && user && sidebarRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Set initial state - only animate opacity and scale, not visibility/display
        gsap.set(sidebarRef.current, { 
          x: 0, 
          opacity: 1
        });
        gsap.set(logoRef.current, { opacity: 0, scale: 0.8 });
        
        // Get all navigation elements including the dropdown button
        const allNavElements = navItemsRef.current.filter(Boolean);
        gsap.set(allNavElements, { opacity: 0, x: -20 });

        // Create timeline
        const tl = gsap.timeline();

        // Logo entrance animation
        tl.to(logoRef.current, {
          duration: 0.8,
          scale: 1,
          opacity: 1,
          ease: "back.out(1.4)",
        })
        // Animate navigation items with stagger
        .to(allNavElements, {
          duration: 0.5,
          x: 0,
          opacity: 1,
          stagger: 0.1,
          ease: "power2.out",
        }, "-=0.3");

      }, 100);

      return () => {
        clearTimeout(timer);
        const allNavElements = navItemsRef.current.filter(Boolean);
        gsap.killTweensOf([sidebarRef.current, logoRef.current, ...allNavElements]);
      };
    }
  }, [loading, user])

  // Hover animations removed for minimalist design

  if (loading || !user) return (
    <div className="fixed top-4 left-4 z-50">
      <div className="w-12 h-12">
        <Lottie
          animationData={loadingAnimation}
          loop={true}
          autoplay={true}
        />
      </div>
    </div>
  )

  // determine roles
  const isAdmin =
    user.role === "admin" ||
    (typeof isAdminEmail === "function" && isAdminEmail(user.email))
  const isCampaignManager = user.role === "campaign_manager"
  const isBackoffice = user.role === "backoffice"

  // build nav list with enhanced styling
  const navItems = [
    { 
      title: "Home", 
      to: "/", 
      icon: Icons.house,
      gradient: "from-blue-500 to-indigo-600",
      description: "Dashboard principale"
    },
    ...(isAdmin
      ? [
          { 
            title: "Gestione utenti", 
            to: "/users", 
            icon: Icons.users,
            gradient: "from-purple-500 to-pink-600",
            description: "Amministra utenti"
          },
          { 
            title: "Leads Campagne", 
            to: "/leads-campagne", 
            icon: Icons.userList,
            gradient: "from-green-500 to-teal-600",
            description: "Monitora leads"
          },
          {
              title: "Preventivatore",
              icon: Icons.calculator,
              gradient: "from-indigo-500 to-teal-600",
              description: "Calcola preventivi",
              flyout: true,
              flyoutItems: [
                { title: "Prestito Personale", to: "/preventivatore-prestito-personale", description: "Calcola preventivo prestito" },
                { title: "Mutuo", to: "/preventivatore-mutuo", description: "Calcola preventivo mutuo" },
              ]
          },
          {
            title: "Richiesta Documenti",
            to: "/document-request",
            icon: Icons.fileText,
            gradient: "from-indigo-500 to-purple-600",
            description: "Crea richiesta documenti"
          },
          {
            title: "Le Mie Richieste Documenti",
            to: "/my-document-requests",
            icon: Icons.files,
            gradient: "from-cyan-500 to-blue-600",
            description: "Visualizza le tue richieste"
          },
          {
            title: "Tutti i Documenti",
            to: "/tutti-i-documenti",
            icon: Icons.files,
            gradient: "from-teal-500 to-cyan-600",
            description: "Visualizza tutti i documenti"
          },
          {
            title: "Impostazioni",
            to: "/settings",
            icon: Icons.gear,
            gradient: "from-gray-500 to-slate-600",
            description: "Configura sistema"
          },
          {
            title: "Email Marketing Nurturing",
            to: "/email-marketing",
            icon: Icons.envelopeSimple,
            gradient: "from-orange-500 to-red-600",
            description: "Campagne email"
          },

        ]
      : isCampaignManager
      ? [
          { 
            title: "Leads Campagna", 
            to: "/campaign-leads", 
            icon: Icons.megaphone,
            gradient: "from-yellow-500 to-orange-600",
            description: "Leads campagna"
          },
          {
            title: "Impostazioni",
            to: "/settings",
            icon: Icons.gear,
            gradient: "from-gray-500 to-slate-600",
            description: "Configura account"
          },
        ]
      : isBackoffice
      ? [
          {
            title: "Richiesta Documenti",
            to: "/backoffice/document-request",
            icon: Icons.fileText,
            gradient: "from-indigo-500 to-purple-600",
            description: "Crea richiesta documenti"
          },
          {
            title: "Le Mie Richieste Documenti",
            to: "/backoffice/my-document-requests",
            icon: Icons.files,
            gradient: "from-cyan-500 to-blue-600",
            description: "Visualizza le tue richieste"
          },
          {
            title: "Tutti i Documenti",
            to: "/backoffice/documenti",
            icon: Icons.files,
            gradient: "from-teal-500 to-cyan-600",
            description: "Visualizza tutti i documenti"
          },
          {
            title: "Impostazioni",
            to: "/backoffice/settings",
            icon: Icons.gear,
            gradient: "from-gray-500 to-slate-600",
            description: "Configura backoffice"
          },
          {
            title: "Preventivatore",
            icon: Icons.calculator,
            gradient: "from-indigo-500 to-teal-600",
            description: "Calcola preventivi",
            flyout: true,
            flyoutItems: [
              { title: "Prestito Personale", to: "/backoffice/preventivatore-prestito-personale", description: "Calcola preventivo prestito" },
              { title: "Mutuo", to: "/backoffice/preventivatore-mutuo", description: "Calcola preventivo mutuo" },
            ]
          }
        ]
      : [
          // Show "My Leads" for agents with pages or confirmed leads, or while checking
          ...((user?.pages && user.pages.length > 0) || hasAssignedLeads || (user?.role === "agent" && !leadsCheckCompleted)) ? [
            { 
              title: "My Leads", 
              to: "/agent/my-leads", 
              icon: Icons.userList,
              gradient: "from-orange-500 to-red-600",
              description: leadsCheckLoading ? "Caricamento..." : "I tuoi leads"
            }
          ] : [],
          {
            title: "Preventivatore",
            icon: Icons.calculator,
            gradient: "from-indigo-500 to-teal-600",
            description: "Calcola preventivi",
            flyout: true,
            flyoutItems: [
              { title: "Prestito Personale", to: "/preventivatore-prestito-personale", description: "Calcola preventivo prestito" },
              { title: "Mutuo", to: "/preventivatore-mutuo", description: "Calcola preventivo mutuo" },
            ]
          },
          {
            title: "Richiesta Documenti",
            to: "/document-request",
            icon: Icons.fileText,
            gradient: "from-indigo-500 to-purple-600",
            description: "Crea richiesta documenti"
          },
          {
            title: "Apri un ticket IT",
            to: "/agent/tickets",
            icon: Icons.ticket,
            gradient: "from-indigo-500 to-teal-600",
            description: "Apri un ticket di assistenza IT"
          },
          {
            title: "Le Mie Richieste Documenti",
            to: "/my-document-requests",
            icon: Icons.files,
            gradient: "from-cyan-500 to-blue-600",
            description: "Visualizza le tue richieste"
          },
          {
            title: "Impostazioni",
            to: "/settings",
            icon: Icons.gear,
            gradient: "from-gray-500 to-slate-600",
            description: "Configura profilo"
          },
        ]),
  ]

  const finalNavItems = navItems;

  return (
    <>
      {/* Mobile Hamburger */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden fixed top-4 left-4 z-50 bg-blue-50/60 backdrop-blur-sm shadow-sm"
          >
            <Icons.menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-64 bg-gradient-to-b from-blue-50/40 via-purple-50/30 to-pink-50/40">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6">
              <Link to="/" onClick={() => setOpen(false)}>
                <img src={logo} alt="Accelera" className="h-10" />
              </Link>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="">
                  <Icons.x className="h-6 w-6" />
                </Button>
              </SheetTrigger>
            </div>

            {/* Nav */}
            <ScrollArea className="flex-1 p-4">
              <nav className="space-y-2">
                {finalNavItems.map(({ title, to, icon: Icon, disabled, gradient, description, flyout, flyoutItems }, index) => {
                  if (flyout) {
                    const isActive = flyoutItems?.some(i => location.pathname === i.to)
                    return (
                      <div key={title}>
                        <button
                          className={cn(
                            "flex items-center px-4 py-3 text-sm font-medium rounded-xl w-full",
                            isActive
                              ? "bg-blue-100/60 text-gray-900 shadow-sm"
                              : "text-gray-700 bg-white/50"
                          )}
                          onClick={() => setMobilePrevOpen(v => !v)}
                        >
                          <div className="flex items-center w-full">
                            <Icon className="mr-3 h-5 w-5" />
                            <div className="flex-1 text-left">
                              <div className="font-semibold">{title}</div>
                              <div className="text-xs opacity-75">{description}</div>
                            </div>
                            <Icons.chevronRight className={cn("h-4 w-4 opacity-50 transition-transform", mobilePrevOpen && "rotate-90")} />
                          </div>
                        </button>
                        {mobilePrevOpen && (
                          <div className="ml-6 mt-1 space-y-1">
                            {flyoutItems.map(({ title: subTitle, to: subTo, description: subDesc }) => (
                              <Link
                                key={subTo}
                                to={subTo}
                                className={cn(
                                  "flex items-start px-3 py-2 text-sm font-medium rounded-lg",
                                  location.pathname === subTo
                                    ? "bg-blue-100/60 text-gray-900"
                                    : "text-gray-700 bg-white/40"
                                )}
                              >
                                <div>
                                  <div className="font-semibold">{subTitle}</div>
                                  <div className="text-xs opacity-75">{subDesc}</div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                  return (
                    <Link
                      key={to}
                      to={to}
                      className={cn(
                        "flex items-center px-4 py-3 text-sm font-medium rounded-xl",
                        disabled
                          ? "text-gray-400 cursor-not-allowed bg-gray-50/40"
                          : location.pathname === to
                          ? "bg-blue-100/60 text-gray-900 shadow-sm"
                          : "text-gray-700 bg-white/50"
                      )}
                      onClick={e => disabled && e.preventDefault()}
                    >
                      <div className="flex items-center w-full">
                        <Icon className="mr-3 h-5 w-5" />
                        <div className="flex-1">
                          <div className="font-semibold">{title}</div>
                          <div className="text-xs opacity-75">{description}</div>
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </nav>
            </ScrollArea>

            {/* Logout */}
            <div className="p-4">
              <Button
                variant="ghost"
                className="w-full justify-start bg-red-100/60 text-red-600"
                onClick={logout}
              >
                <Icons.logOut className="mr-3 h-5 w-5" />
                <span className="font-semibold">Logout</span>
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Static Sidebar for lg+ */}
      <aside 
        ref={sidebarRef}
        className="hidden lg:flex flex-col w-64 h-screen bg-gradient-to-b from-blue-50/40 via-purple-50/30 to-pink-50/40 fixed left-0 top-0 z-50 backdrop-blur-sm"
        style={{ 
          backgroundColor: 'rgba(239, 246, 255, 0.4)'
        }}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-center p-8">
          <Link to="/" className="relative">
            <img 
              ref={logoRef}
              src={logo} 
              alt="Accelera" 
              className="h-12"
              style={{ opacity: 0 }}
            />
          </Link>
        </div>
        
        <nav className="flex-1 p-6 space-y-2 overflow-y-auto">
          {finalNavItems.map(({ title, to, icon: Icon, disabled, gradient, description, flyout, flyoutItems }, index) => {
            if (flyout) {
              const isActive = flyoutItems?.some(i => location.pathname === i.to)
              return (
                <Popover key={title}>
                  <PopoverTrigger asChild>
                    <button
                      ref={(el) => (navItemsRef.current[index] = el)}
                      className={cn(
                        "flex items-center px-4 py-4 text-sm font-medium rounded-xl w-full",
                        isActive
                          ? "bg-blue-100/60 text-gray-900 shadow-sm"
                          : "text-gray-700 bg-white/50"
                      )}
                      style={{ opacity: 0 }}
                    >
                      <div className="flex items-center w-full">
                        <Icon className="mr-4 h-5 w-5" />
                        <div className="flex-1 text-left">
                          <div className="font-semibold">{title}</div>
                          <div className="text-xs opacity-75 mt-1">{description}</div>
                        </div>
                        <Icons.chevronRight className="h-4 w-4 opacity-50" />
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="right" align="start" className="w-56 p-2">
                    <div className="space-y-1">
                      {flyoutItems.map(({ title: subTitle, to: subTo, description: subDesc }) => (
                        <Link
                          key={subTo}
                          to={subTo}
                          className={cn(
                            "flex items-start px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                            location.pathname === subTo
                              ? "bg-blue-100/60 text-gray-900"
                              : "text-gray-700 hover:bg-gray-100"
                          )}
                        >
                          <div>
                            <div className="font-semibold">{subTitle}</div>
                            <div className="text-xs opacity-75 mt-0.5">{subDesc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )
            }
            return (
              <Link
                key={to}
                to={to}
                ref={(el) => (navItemsRef.current[index] = el)}
                className={cn(
                  "flex items-center px-4 py-4 text-sm font-medium rounded-xl",
                  disabled
                    ? "text-gray-400 cursor-not-allowed bg-gray-50/40"
                    : location.pathname === to
                    ? "bg-blue-100/60 text-gray-900 shadow-sm"
                    : "text-gray-700 bg-white/50"
                )}
                style={{
                  opacity: 0
                }}
                onClick={e => disabled && e.preventDefault()}
              >
                <div className="flex items-center w-full">
                  <Icon className="mr-4 h-5 w-5" />
                  <div className="flex-1">
                    <div className="font-semibold">{title}</div>
                    <div className="text-xs opacity-75 mt-1">{description}</div>
                  </div>
                </div>
              </Link>
            )
          })}
        </nav>
        
        {/* Logout Section */}
        <div className="p-6 bg-gradient-to-b from-white/60 to-blue-100/50">
          <Button
            variant="ghost"
            className="w-full justify-start bg-red-100/60 text-red-600"
            onClick={logout}
          >
            <Icons.logOut className="mr-4 h-5 w-5" />
            <span className="font-semibold">Logout</span>
          </Button>
        </div>
      </aside>
    </>
  )
}
